import { r, s, typographyTokens, typeScale } from '@/theme/tokens';
import { metricTrendCardCompactLayout } from './MetricTrendCardFrame.layout';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { SxProps, Theme } from '@/ui/primitives/types';

type ThemeSxObject = Exclude<SxProps<Theme>, readonly unknown[] | ((theme: Theme) => unknown)>;
type ThemeSxFn = (theme: Theme) => ThemeSxObject;

type MetricValueTextSxOptions = {
  align: 'left' | 'right';
  color: string;
  fontSize?: string;
  lineHeight?: string | number;
  nowrap: boolean;
};

type MetricValueRowSxOptions = {
  stackSecondaryValue: boolean;
};

type MetricSecondaryValueSxOptions = {
  stackSecondaryValue: boolean;
};

type MetricDeltaSxOptions = {
  tone: MetricCardItem['countDeltaTone'];
  hidden?: boolean;
};

type MetricDeltaGroupSxOptions = {
  stackDeltaContent: boolean;
};

type StatusMetricCardCountSxOptions = {
  isMissing: boolean;
};

type StatusMetricCardDeltaTextSxOptions = {
  color: string;
};

type StatusMetricCardDeltaIconSxOptions = {
  color: string;
};

export const STATUS_METRIC_CARD_MIN_HEIGHT = metricTrendCardCompactLayout.minHeight;

export const STATUS_METRIC_CARD_CHART_HEIGHT = metricTrendCardCompactLayout.chartHeight;

export const STATUS_METRIC_CARD_META_FONT_SIZE = '13px';

export const STATUS_METRIC_CARD_TREND_FONT_SIZE = '12px';

export const buildMetricValueTextSx =
  ({ align, color, fontSize, lineHeight, nowrap }: MetricValueTextSxOptions): ThemeSxFn =>
  (theme) => ({
    color,
    fontSize: fontSize ?? theme.typography.subtitle1.fontSize,
    lineHeight: lineHeight ?? theme.typography.subtitle1.lineHeight,
    textAlign: align,
    whiteSpace: nowrap ? 'nowrap' : undefined,
  });

export const STATUS_METRIC_CARD_VALUE_ROW_SX = {
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: s('sm'),
  rowGap: s('xxs'),
} as const satisfies ThemeSxObject;

export const buildStatusMetricCardCountSx = ({
  isMissing,
}: StatusMetricCardCountSxOptions): ThemeSxObject => ({
  color: isMissing ? 'text.secondary' : undefined,
  fontSize: isMissing ? '15px' : { xs: 22, xl: 24 },
  lineHeight: 1.15,
  fontWeight: isMissing
    ? (theme: Theme) => theme.typography.fontWeightRegular
    : typographyTokens.metricValue.fontWeight,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
});

export const STATUS_METRIC_CARD_META_TEXT_SX = {
  color: 'text.secondary',
  fontSize: STATUS_METRIC_CARD_META_FONT_SIZE,
  lineHeight: 1.15,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
} as const satisfies ThemeSxObject;

export const buildStatusMetricCardDeltaTextSx = ({
  color,
}: StatusMetricCardDeltaTextSxOptions): ThemeSxObject => ({
  color,
  fontSize: STATUS_METRIC_CARD_TREND_FONT_SIZE,
  lineHeight: 1.2,
  fontWeight: typographyTokens.body.fontWeight,
  whiteSpace: 'nowrap',
});

export const STATUS_METRIC_CARD_EMPTY_DELTA_TEXT_SX = buildStatusMetricCardDeltaTextSx({
  color: 'text.secondary',
});

export const STATUS_METRIC_CARD_DELTA_ICON_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  lineHeight: 1,
} as const satisfies ThemeSxObject;

export const buildStatusMetricCardDeltaIconSx = ({
  color,
}: StatusMetricCardDeltaIconSxOptions): ThemeSxObject => ({
  color,
});

export const STATUS_METRIC_CARD_DELTA_ARROW_ICON_SX = {
  fontSize: 16,
  strokeWidth: 0.5,
} as const satisfies ThemeSxObject;

export const STATUS_METRIC_CARD_BLANK_CHART_SX = {
  flex: 1,
  minHeight: STATUS_METRIC_CARD_CHART_HEIGHT,
  borderRadius: r('sm'),
  backgroundColor: 'var(--surface-card)',
} as const satisfies ThemeSxObject;

export const STATUS_METRIC_CARD_FOOTER_SX = {
  columnGap: s('sm'),
  rowGap: s('xxs'),
} as const satisfies ThemeSxObject;

export const buildMetricSecondaryValueNumberSx =
  ({ hasLabel = true }: { hasLabel?: boolean } = {}): ThemeSxFn =>
  (theme) => ({
    fontSize: theme.typography.pxToRem(17),
    fontWeight: hasLabel ? 400 : undefined,
  });

export const METRIC_VALUE_ROW_MAIN_SX = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: `calc(${s('sm')} + ${s('xs')})`,
  pl: s('xs'),
  minWidth: 0,
} as const satisfies ThemeSxObject;

export const buildMetricValueRowSx = ({
  stackSecondaryValue,
}: MetricValueRowSxOptions): ThemeSxObject => ({
  display: 'grid',
  gridTemplateColumns: stackSecondaryValue ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto',
  alignItems: 'center',
  columnGap: s('md'),
  rowGap: stackSecondaryValue ? s('xxs') : s('none'),
  pt: s('none'),
  minWidth: 0,
});

export const buildMetricSecondaryValueSx = ({
  stackSecondaryValue,
}: MetricSecondaryValueSxOptions): ThemeSxObject => ({
  pt: stackSecondaryValue ? s('none') : '2px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: stackSecondaryValue ? 'flex-start' : 'flex-end',
  justifySelf: stackSecondaryValue ? 'start' : 'end',
  minWidth: 0,
  textAlign: stackSecondaryValue ? 'left' : 'right',
  pl: stackSecondaryValue ? s('xs') : s('none'),
});

export const METRIC_SECONDARY_VALUE_TEXT_SIZE = `${typeScale.lg}px`;

export const STATUS_METRIC_FOOTER_ROW_SX = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'end',
  columnGap: s('md'),
  rowGap: s('none'),
  minWidth: 0,
} as const satisfies ThemeSxObject;

export const buildMetricDeltaGroupSx = ({
  stackDeltaContent,
}: MetricDeltaGroupSxOptions): ThemeSxObject => ({
  display: 'flex',
  flexDirection: stackDeltaContent ? 'column' : 'row',
  alignItems: stackDeltaContent ? 'flex-start' : 'flex-end',
  flexWrap: stackDeltaContent ? 'nowrap' : 'wrap',
  gap: s('sm'),
  rowGap: s('xxs'),
  pl: s('none'),
  minWidth: 0,
});

export const buildMetricDeltaTextSx =
  ({ hidden = false, tone }: MetricDeltaSxOptions): ThemeSxFn =>
  (theme) => ({
    visibility: hidden ? 'hidden' : undefined,
    color:
      tone === 'positive' ? 'success.main' : tone === 'negative' ? 'error.main' : 'text.secondary',
    fontSize: theme.typography.subtitle1.fontSize,
    lineHeight: theme.typography.subtitle1.lineHeight,
    whiteSpace: 'nowrap',
  });
